import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import Textarea from 'components/Form/Textarea';
import DateRange from 'components/Form/DateRange';
import Text from 'components/Form/Text';
import { SubstanceThcPublicSafetyItemModel } from 'forms/sections/substance/thc/publicSafety/types';
import {
  CommonOnUpdate, CommonScrollIntoView, CommonOnError, CommonRequired
} from 'types/props';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import Dropdown from 'components/Form/Dropdown';
import { criminalJusticeorPublicSafetyPosition } from 'constants/enums/substanceOptions';

interface Props extends Partial<SubstanceThcPublicSafetyItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  scrollIntoView?: CommonScrollIntoView
  onError?: CommonOnError
  required?: CommonRequired
  formType?: string
}

export default class ThcPublicSafetyUse extends ValidationElement<Props> {
  update = (updateValues: Partial<SubstanceThcPublicSafetyItemModel>) => {
    if (this.props.onUpdate) {
      this.props.onUpdate({
        Description: this.props.Description,
        InvolvementDates: this.props.InvolvementDates,
        EstimatedUse: this.props.EstimatedUse,
        Comments: this.props.Comments,
        UseWhileEmployedPosition: this.props.UseWhileEmployedPosition, // PVQOnly
        UseWhileEmployedPositionOther: this.props.UseWhileEmployedPositionOther, // PVQOnly
        HowManyTimes: this.props.HowManyTimes, // PVQOnly
        Reason: this.props.Reason, // PVQOnly
        ...updateValues,
      });
    }
  };

  updateDescription = (values: SubstanceThcPublicSafetyItemModel['Description']) => {
    this.update({ Description: values });
  };

  updateInvolvementDates = (values: SubstanceThcPublicSafetyItemModel['InvolvementDates']) => {
    this.update({ InvolvementDates: values });
  };

  updateEstimatedUse = (values: SubstanceThcPublicSafetyItemModel['EstimatedUse']) => {
    this.update({ EstimatedUse: values });
  };

  updateComments = (values: SubstanceThcPublicSafetyItemModel['Comments']) => {
    this.update({ Comments: values });
  };

  updateUseWhileEmployedPosition = (values: SubstanceThcPublicSafetyItemModel['UseWhileEmployedPosition']) => {
    this.update({ UseWhileEmployedPosition: values });
  };

  updateUseWhileEmployedPositionOther = (values: SubstanceThcPublicSafetyItemModel['UseWhileEmployedPositionOther']) => {
    this.update({ UseWhileEmployedPositionOther: values });
  };

  updateHowManyTimes = (values: SubstanceThcPublicSafetyItemModel['HowManyTimes']) => {
    this.update({ HowManyTimes: values });
  };

  updateReason = (values: SubstanceThcPublicSafetyItemModel['Reason']) => {
    this.update({ Reason: values });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;

    return (
      <div className="thc-public-safety-use">

        <Field
          attribute={`${attribute}.InvolvementDates`}
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            {...this.props.InvolvementDates}
            attribute={`${attribute}.InvolvementDates`}
            className="involvement-dates"
            fromDateLabel={i18n.t('substance.pvq.thc.publicSafety.heading.involvementDatesFrom')}
            hideFromDay
            hideToDay
            minDateEqualTo
            name="InvolvementDates"
            onError={this.props.onError}
            onUpdate={this.updateInvolvementDates}
            required={this.props.required}
            toDateLabel={i18n.t('substance.pvq.thc.publicSafety.heading.involvementDatesTo')}
            usePvqFromDate
            usePvqToDate
            allowPresent={false}
          />
        </Field>
        <Field
          title={i18n.t('substance.pvq.thc.publicSafety.heading.frequency')}
          attribute={`${attribute}.EstimatedUse`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            {...this.props.EstimatedUse}
            name="EstimatedUse"
            attribute={`${attribute}.EstimatedUse`}
            className="estimated-use"
            onUpdate={this.updateEstimatedUse}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Field
          title={i18n.t('substance.pvq.thc.publicSafety.heading.howManyTimes')}
          attribute={`${attribute}.HowManyTimes`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            {...this.props.HowManyTimes}
            name="HowManyTimes"
            attribute={`${attribute}.HowManyTimes`}
            className="how-many-times"
            onUpdate={this.updateHowManyTimes}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('substance.pvq.thc.publicSafety.heading.useWhileEmployedPosition')}
          attribute={`${attribute}.UseWhileEmployedPosition`}
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="buttons"
          shrink
          className="no-margin-bottom"
        >
          <Dropdown
            name="UseWhileEmployedPosition"
            attribute={`${attribute}.UseWhileEmployedPosition`}
            className="UseWhileEmployedPosition"
            onUpdate={this.updateUseWhileEmployedPosition}
            onError={this.props.onError}
            value={this.props.UseWhileEmployedPosition?.value}
            required={this.props.required}
          >
            {criminalJusticeorPublicSafetyPosition.map((s) => {
              return (<option key={s} value={s}>
                {s}
              </option>);
            })}
          </Dropdown>
        </Field>
        {/*
        {this.props.UseWhileEmployedPosition?.value === 'Other' && (
          <Field
            title={i18n.t('substance.pvq.thc.publicSafety.heading.useWhileEmployedPositionOther')}
            attribute={`${attribute}.UseWhileEmployedPositionOther`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Text
              {...this.props.UseWhileEmployedPositionOther}
              name="UseWhileEmployedPositionOther"
              attribute={`${attribute}.UseWhileEmployedPositionOther`}
              className="UseWhileEmployedPositionOther"
              onUpdate={this.updateUseWhileEmployedPosition}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )} */}

        <Field
          title={i18n.t('substance.pvq.thc.publicSafety.heading.reason')}
          attribute={`${attribute}.Reason`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            {...this.props.Reason}
            name="Reason"
            attribute={`${attribute}.Reason`}
            className="reason"
            onUpdate={this.updateReason}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
      </div>
    );
  }
}

ThcPublicSafetyUse.defaultProps = {
  onError: (value, arr) => arr,
  Comments: {},
  formType: '',
  UseWhileEmployedPosition: {}, // PVQOnly
  UseWhileEmployedPositionOther: {}, // PVQOnly
  HowManyTimes: {}, // PVQOnly
  Reason: {}, // PVQOnly
};
